import { useEffect, useMemo, useState } from 'react'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import { getCompanyDetail, listCompanies } from '../api/companyApi'

const DEFAULT_PAGE_SIZE = 10
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000'

function toDisplayDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  })
}

function normalizeStatus(item) {
  if (Number(item?.is_archive) === 1) return 'Archive / Deactive'
  if (Number(item?.is_approved) === 0) return 'Pending Review'
  return 'Active'
}

function toAssetUrl(path) {
  if (!path) return ''
  if (path.startsWith('http://') || path.startsWith('https://')) return path

  if (path.startsWith('/')) {
    return `${API_BASE_URL}${path}`
  }

  return `${API_BASE_URL}/${path}`
}

export function useCompanyListScreen() {
  const [searchText, setSearchText] = useState('')
  const [gstText, setGstText] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(DEFAULT_PAGE_SIZE)

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [items, setItems] = useState([])
  const [meta, setMeta] = useState({
    totalCount: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasNext: false,
  })

  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState('')
  const [previewData, setPreviewData] = useState(null)

  const queryParams = useMemo(() => {
    const params = {
      page,
      page_size: pageSize,
      include_total: true,
    }

    const searchValue = [searchText.trim(), gstText.trim()].filter(Boolean).join(' ')
    if (searchValue) {
      params.search = searchValue
    }

    if (statusFilter === 'active') {
      params.archive = 0
    } else if (statusFilter === 'pending') {
      params.pending_review = true
      params.archive = 0
    } else if (statusFilter === 'archive') {
      params.archive = 1
    }

    if (fromDate) {
      params.from_date = fromDate
      params.fromdate = fromDate
    }

    if (toDate) {
      params.to_date = toDate
      params.enddate = toDate
    }

    return params
  }, [fromDate, gstText, page, pageSize, searchText, statusFilter, toDate])

  useEffect(() => {
    let active = true

    async function loadCompanies() {
      setIsLoading(true)
      setError('')

      try {
        const response = await listCompanies(queryParams)
        if (!active) return

        const rows = Array.isArray(response?.items) ? response.items : []
        setItems(rows)
        setMeta({
          totalCount: Number(response?.total_count || 0),
          page: Number(response?.page || page),
          pageSize: Number(response?.page_size || pageSize),
          hasNext: Boolean(response?.has_next),
        })
      } catch (requestError) {
        if (!active) return
        setError(extractApiErrorMessage(requestError, 'Failed to load companies.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadCompanies()

    return () => {
      active = false
    }
  }, [page, pageSize, queryParams])

  const totalPages = useMemo(() => {
    if (!meta.totalCount || !meta.pageSize) return meta.hasNext ? page + 1 : page
    return Math.max(1, Math.ceil(meta.totalCount / meta.pageSize))
  }, [meta.hasNext, meta.pageSize, meta.totalCount, page])

  const rows = useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        name: item.name || '-',
        companyCode: item.company_code || '-',
        gstTaxNumber: item.gst_tax_number || '-',
        logoUrl: toAssetUrl(item.company_logo_path),
        status: normalizeStatus(item),
        createdBy: item.created_by_name || (item.created_by ? 'User' : '-'),
        createdAt: toDisplayDate(item.created_at),
      })),
    [items],
  )

  const rangeStart = useMemo(() => {
    if (!rows.length) return 0
    return (page - 1) * pageSize + 1
  }, [page, pageSize, rows.length])

  const rangeEnd = useMemo(() => {
    if (!rows.length) return 0
    return rangeStart + rows.length - 1
  }, [rangeStart, rows.length])

  function handleStatusFilterChange(nextFilter) {
    setStatusFilter(nextFilter)
    setPage(1)
  }

  function handleSearchTextChange(value) {
    setSearchText(value)
    setPage(1)
  }

  function handleGstTextChange(value) {
    setGstText(value)
    setPage(1)
  }

  function handleFromDateChange(value) {
    setFromDate(value)
    setPage(1)
  }

  function handleToDateChange(value) {
    setToDate(value)
    setPage(1)
  }

  function goToPage(nextPage) {
    if (nextPage < 1) return
    if (totalPages && nextPage > totalPages) return
    setPage(nextPage)
  }

  function goToPreviousPage() {
    setPage((prev) => Math.max(1, prev - 1))
  }

  function goToNextPage() {
    if (meta.hasNext || page < totalPages) {
      setPage((prev) => prev + 1)
    }
  }

  async function openPreview(companyId) {
    setIsPreviewOpen(true)
    setIsPreviewLoading(true)
    setPreviewError('')
    setPreviewData(null)

    try {
      const detail = await getCompanyDetail(companyId)
      setPreviewData({
        ...detail,
        logoUrl: toAssetUrl(detail?.company_logo_path),
      })
    } catch (requestError) {
      setPreviewError(extractApiErrorMessage(requestError, 'Failed to load company preview.'))
    } finally {
      setIsPreviewLoading(false)
    }
  }

  function closePreview() {
    setIsPreviewOpen(false)
    setPreviewError('')
    setPreviewData(null)
  }

  return {
    state: {
      searchText,
      gstText,
      statusFilter,
      fromDate,
      toDate,
      page,
      pageSize,
      totalPages,
      totalCount: meta.totalCount,
      hasNext: meta.hasNext,
      isLoading,
      error,
      rows,
      rangeStart,
      rangeEnd,
      isPreviewOpen,
      isPreviewLoading,
      previewError,
      previewData,
    },
    actions: {
      setSearchText: handleSearchTextChange,
      setGstText: handleGstTextChange,
      setStatusFilter: handleStatusFilterChange,
      setFromDate: handleFromDateChange,
      setToDate: handleToDateChange,
      goToPage,
      goToPreviousPage,
      goToNextPage,
      openPreview,
      closePreview,
    },
  }
}
